import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFileExplorerContext } from '../../FileExplorerContext';
import { FileCard } from './FileCard';
import { actionsFor, canCheckFile } from '../../modules/FileActionsMenu';
import { FolderCard } from './FolderCard';
import { getFolderLink } from '../../../../utils/getFolderName';

/**
 * GridView - Grid/card-based file/folder listing
 *
 * Features:
 * - Responsive grid layout (3-4 columns on desktop, 1-2 on mobile)
 * - Image thumbnails for image files
 * - File type icons for other files
 * - Folder cards
 * - Selection support
 */
export const GridView = ({
  folders = [],
  files = [],
  onFolderClick,
  onFileClick,
  onFileAction,
  fileActions,
  folderActions,
  onFolderAction,
  onRename,
  parentFolderName,
  excludedFileIds = [],
  getDragData,
  isOver,
  canDrop = false,
  checkable = false,
  checkedIds = [],
  onToggleCheck,
  emptyState,
}) => {
  const { t } = useTranslation(['files', 'common']);
  const { selectedFiles, isFileSelected, handleFileSelect, selectionMode } = useFileExplorerContext();

  const showCheckbox = selectionMode === 'multi' && selectedFiles.length > 0;

  const isPrimarySelected = (file) => {
    return selectionMode === 'compare' ? selectedFiles[0]?.file_id === file.file_id : isFileSelected(file);
  };

  const isSecondarySelected = (file) => {
    return selectionMode === 'compare' && selectedFiles[1]?.file_id === file.file_id;
  };

  const handleFolderCardClick = (folder) => {
    if (onFolderClick) {
      onFolderClick(folder);
    }
  };

  const handleFileCardClick = (file, event) => {
    // Don't trigger if clicking on checkbox
    if (event?.target?.type === 'checkbox') {
      return;
    }

    handleFileSelect(file);

    if (onFileClick) {
      onFileClick(file);
    }
  };

  // Flatten categorized files if needed
  let allFiles = files;
  if (files.length > 0 && files[0]?.date) {
    // Categorized format - flatten
    allFiles = files.flatMap((category) => category.attachments || []);
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Empty state */}
      {folders.length === 0 && allFiles.length === 0 ? (
        <Box
          sx={{
            py: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {emptyState || (
            <Typography variant="body1" color="text.secondary">
              {t('files:empty_folder')}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: 2,
          }}
        >
          {/* Folders */}
          {folders.map((folder) => (
            <FolderCard
              key={folder.folder_id}
              folder={folder}
              link={getFolderLink(folder, parentFolderName)}
              onClick={handleFolderCardClick}
              dragData={getDragData?.(folder, 'folder')}
              isOver={isOver?.(`folder:${folder.folder_id}`)}
              canDrop={canDrop}
              actions={folderActions?.(folder)}
              onAction={onFolderAction}
              onRename={onRename && ((item, name) => onRename('folder', item, name))}
            />
          ))}

          {/* Files */}
          {allFiles.map((file) => (
            <FileCard
              key={file.file_id}
              file={file}
              selected={isPrimarySelected(file)}
              secondarySelected={isSecondarySelected(file)}
              disabled={excludedFileIds.includes(file.file_id)}
              onClick={handleFileCardClick}
              showCheckbox={showCheckbox}
              dragData={getDragData?.(file, 'file')}
              actions={actionsFor(fileActions, file)}
              onFileAction={onFileAction}
              onRename={onRename && ((item, name) => onRename('file', item, name))}
              checkable={checkable && canCheckFile(fileActions, file)}
              checked={checkedIds.includes(file.file_id)}
              onToggleCheck={onToggleCheck}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
